import { Controller, useFormContext } from 'react-hook-form';

import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

// ----------------------------------------------------------------------

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const getHint = (option) =>
  Array.isArray(option?.title) ? option.title.filter(Boolean).join(', ') : option?.title || '';

// Searchable multi-select with checkboxes, chips and a tooltip per option (option.title).
// Works with RHF array-of-string values and `options={[{ label, value, title }]}`.
export function RHFMultiAutocomplete({
  name,
  label,
  options = [],
  placeholder,
  helperText,
  limitTags = 6,
  exclusiveValue,
  handleOnChange,
  hintFirst = false,
  ...other
}) {
  const { control } = useFormContext();

  // When `exclusiveValue` (e.g. '*' = "all") is picked, collapse the selection to just it;
  // picking any other value while it's selected drops it. Mirrors the operator/provider logic.
  const resolveValues = (values) => {
    if (exclusiveValue == null) return values;

    if (values.includes(exclusiveValue) && values[0] !== exclusiveValue) {
      return [exclusiveValue];
    }
    return values.length > 1 ? values.filter((item) => item !== exclusiveValue) : values;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedValues = field.value || [];
        const valueObjects = options.filter((opt) => selectedValues.includes(opt.value));

        return (
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={limitTags}
            options={options}
            value={valueObjects}
            onChange={(event, newValue) => {
              const values = resolveValues(newValue.map((item) => item.value));
              field.onChange(values);
              handleOnChange?.(values);
            }}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            ListboxProps={{ sx: { maxHeight: 320 } }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              const hint = getHint(option);
              const swapped = hintFirst && Boolean(hint);

              return (
                <Tooltip key={key} title={hint} placement="right" arrow>
                  <li {...optionProps}>
                    <Checkbox
                      size="small"
                      disableRipple
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={swapped ? hint : option.label}
                      secondary={(swapped ? option.label : hint) || undefined}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        noWrap: true,
                        sx: { maxWidth: 360 },
                      }}
                    />
                  </li>
                </Tooltip>
              );
            }}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Tooltip key={key} title={getHint(option)} arrow>
                    <Chip size="small" variant="soft" label={option.label} {...tagProps} />
                  </Tooltip>
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                style={{ width: '100%' }}
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error ? error?.message : helperText}
              />
            )}
            {...other}
          />
        );
      }}
    />
  );
}
